import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Download, LogOut, Search, Users, PhoneCall, ArrowLeft, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "eyb_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [registrations, setRegistrations] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    try {
      const [regs, st] = await Promise.all([
        axios.get(`${API}/admin/registrations`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setRegistrations(regs.data.registrations);
      setStats(st.data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load registrations.");
      }
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setRegistrations(null);
    setStats(null);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/registrations/export`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "eyb-registrations.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!registrations) return [];
    const q = search.trim().toLowerCase();
    if (!q) return registrations;
    return registrations.filter((r) =>
      [r.first_name, r.last_name, r.email, r.business_name, r.industry, r.biggest_challenge]
        .filter(Boolean)
        .some((v) => (Array.isArray(v) ? v.join(" ") : v).toLowerCase().includes(q))
    );
  }, [registrations, search]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B2B4B] flex items-center justify-center px-4 grain relative" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl"
        >
          <div className="flex justify-center">
            <img src="/eyb-logo.webp" alt="EYB Consulting" className="h-10 w-auto" />
          </div>
          <h1 className="font-display mt-6 text-xl font-bold text-center text-[#0B2B4B]">Admin portal</h1>
          <p className="mt-1.5 text-center text-sm text-slate-500">Enter the admin password to view registrations.</p>
          <form onSubmit={login} className="mt-6 space-y-4">
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 text-sm outline-none transition-all duration-200 focus:border-[#0FA3E8] focus:ring-4 focus:ring-[#0FA3E8]/15"
                data-testid="admin-password-input"
              />
            </div>
            {loginError && (
              <p className="text-xs font-medium text-red-500" data-testid="admin-login-error">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loggingIn || !password}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B2B4B] py-3 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-[#0FA3E8] disabled:opacity-60"
              data-testid="admin-login-submit-btn"
            >
              {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-[#0284C7]" data-testid="admin-back-link">
              <ArrowLeft className="h-3 w-3" /> Back to site
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="bg-[#0B2B4B] border-b border-[#16345C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="rounded-lg bg-white px-3 py-1.5" data-testid="admin-home-link">
              <img src="/eyb-logo.webp" alt="EYB Consulting" className="h-6 w-auto" />
            </Link>
            <span className="text-sm font-semibold text-white">Registrations</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-full bg-[#0FA3E8] px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-[#0284C7] disabled:opacity-60"
              data-testid="admin-export-csv-btn"
            >
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Export CSV
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors duration-200 hover:text-white hover:border-white/40"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="stat-total">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F0F9FF] text-[#0284C7]">
                <Users className="h-5 w-5" />
              </span>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total registrations</p>
            </div>
            <p className="font-mono-brand mt-4 text-4xl font-semibold text-[#0B2B4B]">{stats ? stats.total : "—"}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="stat-contact-yes">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <PhoneCall className="h-5 w-5" />
              </span>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Open to contact</p>
            </div>
            <p className="font-mono-brand mt-4 text-4xl font-semibold text-[#0B2B4B]">{stats ? stats.contact_yes : "—"}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="stat-top-challenge">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Top challenge</p>
            <p className="mt-4 text-base font-semibold leading-snug text-[#0B2B4B]">
              {stats && Object.keys(stats.by_challenge).length
                ? Object.entries(stats.by_challenge).sort((a, b) => b[1] - a[1])[0][0]
                : "—"}
            </p>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-4 border-b border-slate-100 p-5">
            <h2 className="font-display text-lg font-semibold text-[#0B2B4B]">All registrations</h2>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, business, industry…"
                className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-4 text-sm outline-none transition-all duration-200 focus:border-[#0FA3E8] focus:ring-4 focus:ring-[#0FA3E8]/15"
                data-testid="admin-search-input"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admin-registrations-table">
              <thead>
                <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3.5 font-semibold">Name</th>
                  <th className="px-5 py-3.5 font-semibold">Contact</th>
                  <th className="px-5 py-3.5 font-semibold">Business</th>
                  <th className="px-5 py-3.5 font-semibold">In business</th>
                  <th className="px-5 py-3.5 font-semibold">Biggest challenges</th>
                  <th className="px-5 py-3.5 font-semibold">OK to contact</th>
                  <th className="px-5 py-3.5 font-semibold">Registered</th>
                </tr>
              </thead>
              <tbody>
                {registrations === null ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-slate-400">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-slate-400" data-testid="admin-empty-state">
                      No registrations yet.
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50 transition-colors duration-150 hover:bg-[#F0F9FF]/50" data-testid={`admin-row-${r.id.slice(0, 8)}`}>
                      <td className="px-5 py-4 font-semibold text-[#0F172A]">
                        {r.first_name} {r.last_name}
                      </td>
                      <td className="px-5 py-4 text-slate-500">
                        <div>{r.email}</div>
                        {r.mobile && <div className="text-xs text-slate-400">{r.mobile}</div>}
                      </td>
                      <td className="px-5 py-4 text-slate-600">
                        <div className="font-medium">{r.business_name}</div>
                        <div className="text-xs text-slate-400">{r.industry}</div>
                      </td>
                      <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{r.tenure}</td>
                      <td className="px-5 py-4 text-slate-500 max-w-[16rem]">
                        {Array.isArray(r.biggest_challenge) ? r.biggest_challenge.join(", ") : r.biggest_challenge}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            r.contact_consent === "Yes" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {r.contact_consent}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-slate-400 whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
